import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CCol, CRow } from '@coreui/react-pro';
import { Link, useHistory } from 'react-router-dom';
import moment from 'moment';
import { Modal } from 'react-bootstrap';
import { CXButton, CXDropDown, CXTextArea, CXTextInput } from '../../components/CXForm';
import MultiSelectClearDropdown, { MultiSelectOption } from '../../components/MultiSelectClearDropdown';
import { REPORTS_PATHS } from '../../constants/path';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectUser } from '../../slice/userSlice';
import { pushToast } from '../../slice/appSlice';
import { getCarReportTopics } from '../../services/carReportTopicAPI';
import { uploadFiles } from '../../services/commonAPI';
import {
    createCarReportRecord,
    createCarReportRemark,
} from '../../services/carReportsAPI';
import { extractErrorMessage, formatAddRemarkErrorMessage, formatCreateReportErrorMessage } from '../../utils/apiErrorMessage';
import './style/CARReportCreatePage.scss';
import './style/CARAssigneePage.scss';

type Option = { value: string; text: string };

type CreateFormState = {
    category: string;
    subject: string;
    comment: string;
    ufi: string;
    airport: string;
    chartRef: string;
    requireResponse: 'YES' | 'NO' | '';
    assigneeLead: string;
    assigneeGroup: string[];
    remark: string;
};

type FieldErrors = Partial<Record<'category' | 'subject' | 'comment' | 'ufi', string>>;
type CreateFormTextField = Exclude<keyof CreateFormState, 'assigneeLead' | 'assigneeGroup'>;

const initialFormState: CreateFormState = {
    category: '',
    subject: '',
    comment: '',
    ufi: '',
    airport: '',
    chartRef: '',
    requireResponse: '',
    assigneeLead: '',
    assigneeGroup: [],
    remark: '',
};

const extractTopicList = (res: any): any[] => {
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.topicList)) return res.topicList;
    if (Array.isArray(res?.data?.topicList)) return res.data.topicList;
    return [];
};

const toOptions = (values: (string | undefined)[]): Option[] => {
    const uniq = Array.from(
        new Set(
            (values || [])
                .map((v) => String(v ?? '').trim())
                .filter(Boolean),
        ),
    ).sort((a, b) => a.localeCompare(b));

    return uniq.map((v) => ({ value: v, text: v }));
};

const toMultiSelectOptions = (values: (string | undefined)[]): MultiSelectOption[] =>
    toOptions(values).map((v) => ({ value: v.value, text: v.text }));

const normalizeUploadedFileId = (value: unknown): string => {
    const raw = String(value ?? '').trim();
    if (!raw) return '';
    return raw;
};

const getCreatedId = (resp: any): string => {
    const id =
        resp?.data?.carReport?.id ??
        resp?.data?.carReport?._id ??
        resp?.data?.id ??
        resp?.data?._id ??
        resp?.data?.carReportId ??
        resp?.id ??
        resp?._id ??
        '';
    return String(id ?? '').trim();
};

type CreateFlowError = Error & {
    alreadyNotified?: boolean;
    source?: 'createReport';
};

const RETRY_DELAY_MS = 800;
const REMARK_MAX_ATTEMPTS = 2;
const UFI_MIN_LENGTH = 24;
const UFI_MIN_LENGTH_ERROR_MESSAGE = 'UFI must be at least 24 words.';
const UFI_INVALID_CHAR_ERROR_MESSAGE = 'UFI must contain only letters and numbers.';
const PHOTO_FILE_ERROR_MESSAGE = 'Photo only accepts image files, such as JPG or PNG.';
const PHOTO_ACCEPT_ATTR = 'image/*';
const PHOTO_EXTENSION_ALLOWLIST = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'heic', 'heif', 'tif', 'tiff']);

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getFileExtension = (name: string): string => {
    const normalized = String(name ?? '').trim();
    const lastDot = normalized.lastIndexOf('.');
    if (lastDot < 0) return '';
    return normalized.slice(lastDot + 1).toLowerCase();
};

const isAcceptedPhotoFile = (file: File): boolean => {
    const mimeType = String(file?.type ?? '').trim().toLowerCase();
    if (mimeType.startsWith('image/')) return true;
    return PHOTO_EXTENSION_ALLOWLIST.has(getFileExtension(file?.name ?? ''));
};

const toStatusCode = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
        const parsed = Number(value.trim());
        if (Number.isFinite(parsed)) return parsed;
    }
    return undefined;
};

const toBizCode = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
        const parsed = Number(value.trim());
        if (Number.isFinite(parsed)) return parsed;
    }
    return undefined;
};

const isRetryableStatus = (status?: number): boolean => !!status && (status >= 500 || status === 429);

const isRetryableBizCode = (code?: number): boolean => code === 1000;

const isRetryableNetworkError = (err: any): boolean => {
    const code = String(err?.code ?? '').trim().toUpperCase();
    return code === 'ERR_NETWORK' || code === 'ECONNABORTED' || code === 'ETIMEDOUT' || code === 'ECONNRESET';
};

const isRetryableResponse = (resp: any): boolean => {
    const status = toStatusCode(resp?.status);
    if (isRetryableStatus(status)) return true;

    const bizCode = toBizCode(resp?.code);
    return isRetryableBizCode(bizCode);
};

const buildApiFailureError = (resp: any, fallback: string, source?: CreateFlowError['source']): CreateFlowError => {
    const err = new Error(extractErrorMessage(resp, fallback)) as CreateFlowError;
    const status = toStatusCode(resp?.status);
    err.source = source;
    if (status !== undefined) {
        // sendHttpRequest already dispatched a global error toast for HTTP-level failures.
        err.alreadyNotified = true;
    }
    return err;
};

const assertApiSuccess = (resp: any, fallback: string, source?: CreateFlowError['source']) => {
    const status = toStatusCode(resp?.status);
    if (status !== undefined && status >= 400) {
        throw buildApiFailureError(resp, fallback, source);
    }

    const bizCode = toBizCode(resp?.code);
    if (bizCode !== undefined && bizCode !== 0) {
        throw buildApiFailureError(resp, fallback, source);
    }
};

async function retryRequest<T>(
    task: () => Promise<T>,
    maxAttempts: number,
    shouldRetryResult: (result: T) => boolean,
): Promise<T> {
    let lastResult: T | undefined;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
            const result = await task();
            lastResult = result;

            if (attempt < maxAttempts && shouldRetryResult(result)) {
                await sleep(RETRY_DELAY_MS);
                continue;
            }

            return result;
        } catch (err) {
            if (attempt < maxAttempts && isRetryableNetworkError(err)) {
                await sleep(RETRY_DELAY_MS);
                continue;
            }
            throw err;
        }
    }

    return lastResult as T;
}

const shouldShowLocalErrorToast = (err: any): boolean => !Boolean((err as CreateFlowError)?.alreadyNotified);

const ErrorText = ({ text }: { text?: string }) => {
    return <div className={`errorPlaceholder ${text ? '' : 'car-report-create-page__error-placeholder-empty'}`}>{text || 'placeholder'}</div>;
};

const UploadField = (props: {
    title: string;
    files: File[];
    disabled?: boolean;
    accept?: string;
    inputTestId?: string;
    onFilesAdded: (files: File[]) => void;
    onRemoveFile: (idx: number) => void;
}) => {
    const { title, files, disabled, accept, inputTestId, onFilesAdded, onRemoveFile } = props;

    const handleFilesAdded = (next: File[]) => {
        if (disabled || next.length === 0) return;
        onFilesAdded(next);
    };

    return (
        <div className="car-report-upload-field">
            <div
                className="car-report-upload-field__dropzone"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                    e.preventDefault();
                    const next = Array.from(e.dataTransfer.files || []);
                    handleFilesAdded(next);
                }}
            >
                <div className="car-report-upload-field__dropzone-text">
                    Drop a file here to upload
                </div>
                <label
                    className={`car-report-upload-field__choose-file ${disabled ? 'is-disabled' : ''}`}
                >
                    Or choose file
                    <input
                        type="file"
                        multiple
                        accept={accept}
                        data-testid={inputTestId}
                        className="car-report-upload-field__input"
                        onChange={(e) => {
                            const next = Array.from(e.target.files || []);
                            handleFilesAdded(next);
                            e.target.value = '';
                        }}
                        disabled={disabled}
                    />
                </label>
            </div>

            <div className="car-report-upload-field__title">
                {title}
            </div>
            <div className="car-report-upload-field__list">
                {files.length === 0 ? (
                    <div className="car-report-upload-field__empty">
                        No files uploaded
                    </div>
                ) : (
                    files.map((f, idx) => (
                        <div
                            key={`${f.name}-${idx}`}
                            className="car-report-upload-field__item"
                        >
                            <span className="car-report-upload-field__filename">
                                {f.name}
                            </span>
                            <button
                                type="button"
                                onClick={() => onRemoveFile(idx)}
                                disabled={disabled}
                                className="car-report-upload-field__remove"
                            >
                                x
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default function CARReportCreatePage() {
    const history = useHistory();
    const appDispatch = useAppDispatch();
    const { userId } = useAppSelector(selectUser);

    const creator = useMemo(() => String(userId || '').trim(), [userId]);

    const [form, setForm] = useState<CreateFormState>(initialFormState);
    const [errors, setErrors] = useState<FieldErrors>({});

    const [topicOptionsSource, setTopicOptionsSource] = useState<any[]>([]);
    const [leadOptions, setLeadOptions] = useState<MultiSelectOption[]>([]);
    const [groupOptions, setGroupOptions] = useState<MultiSelectOption[]>([]);

    const [photoFiles, setPhotoFiles] = useState<File[]>([]);
    const [remarkAttachmentFiles, setRemarkAttachmentFiles] = useState<File[]>([]);
    const [remarkErrors, setRemarkErrors] = useState({ content: false });

    const [creating, setCreating] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    useEffect(() => {
        const loadFormOptions = async () => {
            try {
                const topicResp = await getCarReportTopics('', '', '', false);

                const topicList = extractTopicList(topicResp);
                setTopicOptionsSource(topicList);
                const leadSet = new Set<string>();
                const groupSet = new Set<string>();

                topicList.forEach((t: any) => {
                    const type = String(t?.AssigneeType ?? '').trim().toUpperCase();
                    const name = String(t?.AssigneeName ?? '').trim();
                    if (!name) return;
                    if (type === 'ASSIGNEE_LEAD') leadSet.add(name);
                    if (type === 'GROUP') groupSet.add(name);
                });

                setLeadOptions(toMultiSelectOptions(Array.from(leadSet)));
                setGroupOptions(toMultiSelectOptions(Array.from(groupSet)));
            } catch {
                setTopicOptionsSource([]);
                setLeadOptions([]);
                setGroupOptions([]);
            }
        };

        loadFormOptions();
    }, []);

    const categoryToSubjects = useMemo(() => {
        const map = new Map<string, Set<string>>();

        (topicOptionsSource || []).forEach((topic: any) => {
            const category = String(topic?.Category ?? '').trim();
            const subject = String(topic?.Subject ?? '').trim();
            if (!category || !subject) return;

            if (!map.has(category)) {
                map.set(category, new Set<string>());
            }
            map.get(category)!.add(subject);
        });

        return map;
    }, [topicOptionsSource]);

    const categoryOptions = useMemo<Option[]>(
        () => toOptions(Array.from(categoryToSubjects.keys())),
        [categoryToSubjects],
    );

    const subjectOptions = useMemo<Option[]>(() => {
        const selectedCategory = form.category.trim();

        if (selectedCategory) {
            const subjects = categoryToSubjects.get(selectedCategory);
            return toOptions(Array.from(subjects ?? []));
        }

        const allSubjects = Array.from(categoryToSubjects.values()).flatMap((subjects) => Array.from(subjects));
        return toOptions(allSubjects);
    }, [categoryToSubjects, form.category]);

    const validateMandatoryFields = useCallback(() => {
        const nextErrors: FieldErrors = {};
        const hasRemarkContent = Boolean(form.remark.trim());
        const hasRemarkAttachment = remarkAttachmentFiles.length > 0;
        const ufiValue = form.ufi.toUpperCase();
        const nextRemarkErrors = {
            content: false,
        };

        if (!form.category.trim()) nextErrors.category = 'Category may not be blank.';
        if (!form.subject.trim()) nextErrors.subject = 'Subject may not be blank.';
        if (!form.comment.trim()) nextErrors.comment = 'Comment may not be blank.';
        if (!ufiValue.trim()) {
            nextErrors.ufi = 'UFI may not be blank.';
        } else if (!/^[A-Z0-9]+$/.test(ufiValue)) {
            nextErrors.ufi = UFI_INVALID_CHAR_ERROR_MESSAGE;
        } else if (ufiValue.length < UFI_MIN_LENGTH) {
            nextErrors.ufi = UFI_MIN_LENGTH_ERROR_MESSAGE;
        }

        if (hasRemarkAttachment && !hasRemarkContent) {
            nextRemarkErrors.content = true;
        }

        setErrors(nextErrors);
        setRemarkErrors(nextRemarkErrors);
        return Object.keys(nextErrors).length === 0 && !nextRemarkErrors.content;
    }, [form, remarkAttachmentFiles]);

    const handleFormChange = useCallback(
        (field: CreateFormTextField, value: string) => {
            if (field === 'ufi') {
                setForm((prev) => ({ ...prev, ufi: value.toUpperCase() }));
            } else if (field === 'category') {
                setForm((prev) => {
                    const nextCategory = String(value ?? '').trim();
                    const currentSubject = String(prev.subject ?? '').trim();
                    if (!currentSubject) return { ...prev, category: value };

                    const allowedSubjects = categoryToSubjects.get(nextCategory);
                    if (allowedSubjects?.has(currentSubject)) {
                        return { ...prev, category: value };
                    }

                    return { ...prev, category: value, subject: '' };
                });
            } else {
                setForm((prev) => ({ ...prev, [field]: value }));
            }

            if (field === 'remark') {
                setRemarkErrors((prev) => (prev.content ? { ...prev, content: false } : prev));
            }

            if (errors[field as keyof FieldErrors]) {
                setErrors((prev) => ({ ...prev, [field]: undefined }));
            }
        },
        [categoryToSubjects, errors],
    );

    const selectedLeadOptions = useMemo<MultiSelectOption[]>(() => {
        const lead = form.assigneeLead.trim();
        return lead ? [{ value: lead, text: lead }] : [];
    }, [form.assigneeLead]);

    const selectedGroupOptions = useMemo<MultiSelectOption[]>(
        () =>
            (form.assigneeGroup || [])
                .map((group) => String(group ?? '').trim())
                .filter(Boolean)
                .map((group) => ({ value: group, text: group })),
        [form.assigneeGroup],
    );

    const handleRequireResponseChange = useCallback((value: 'YES' | 'NO') => {
        setForm((prev) => ({ ...prev, requireResponse: value }));
    }, []);

    const handlePhotoFilesAdded = useCallback((files: File[]) => {
        if (files.length === 0) return;

        const hasInvalidPhotoFile = files.some((file) => !isAcceptedPhotoFile(file));
        if (hasInvalidPhotoFile) {
            appDispatch(pushToast({ type: 'error', message: PHOTO_FILE_ERROR_MESSAGE }));
            return;
        }

        setPhotoFiles((prev) => [...prev, ...files]);
    }, [appDispatch]);

    const onChangeAssigneeField = useCallback(
        (
            value: MultiSelectOption[],
            field: string,
            state: any,
            setState: (next: any) => void,
        ) => {
            const currentState = state as CreateFormState;
            const nextState: CreateFormState = { ...currentState };

            if (field === 'assigneeLead') {
                const latest = value.length > 0 ? value[value.length - 1] : undefined;
                const nextLead = String(latest?.value ?? '').trim();
                nextState.assigneeLead = nextLead;
                if (nextLead) nextState.assigneeGroup = [];
            }

            if (field === 'assigneeGroup') {
                const nextGroups = value.map((v) => String(v?.value ?? '').trim()).filter(Boolean);
                nextState.assigneeGroup = nextGroups;
                if (nextGroups.length > 0) nextState.assigneeLead = '';
            }

            setState(nextState);
        },
        [],
    );

    const goBackToList = useCallback(() => {
        history.push(REPORTS_PATHS.carReportTracker);
    }, [history]);

    const openCancelConfirm = useCallback(() => {
        if (creating) return;
        setShowCancelConfirm(true);
    }, [creating]);

    const handleBreadcrumbTrackerClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        openCancelConfirm();
    }, [openCancelConfirm]);

    const handleCancelModalBack = useCallback(() => {
        setShowCancelConfirm(false);
    }, []);

    const handleCancelModalConfirm = useCallback(() => {
        setShowCancelConfirm(false);
        goBackToList();
    }, [goBackToList]);

    const handleCreate = useCallback(async () => {
        if (creating) return;

        if (!creator) {
            appDispatch(pushToast({ type: 'error', message: 'Invalid creator. Please re-login.' }));
            return;
        }

        if (!validateMandatoryFields()) return;

        setCreating(true);

        try {
            const cleanedUfi = form.ufi.toUpperCase();
            const cleanedCategory = form.category.trim();
            const cleanedSubject = form.subject.trim();
            const cleanedComment = form.comment.trim();
            const cleanedAirport = form.airport.trim();
            const cleanedChartRef = form.chartRef.trim();
            const cleanedLead = form.assigneeLead.trim();
            const cleanedGroups = form.assigneeGroup.map((group) => String(group ?? '').trim()).filter(Boolean);
            const cleanedRemark = form.remark.trim();

            let photoIDs: string[] = [];

            if (photoFiles.length > 0) {
                const photoUploadResp = await uploadFiles({
                    files: photoFiles,
                    path: cleanedUfi,
                    type: 'carReport',
                });

                assertApiSuccess(photoUploadResp, 'Photo upload failed.');

                if (photoUploadResp?.code !== 0 || !Array.isArray(photoUploadResp?.data?.fileIds)) {
                    throw new Error(photoUploadResp?.msg || photoUploadResp?.message || 'Photo upload failed.');
                }

                const uploadedPhotoFiles = photoUploadResp?.data?.fileIds ?? [];
                photoIDs = uploadedPhotoFiles
                    .map((x) => normalizeUploadedFileId(x?.fileId))
                    .filter(Boolean);
            }

            const createResp = await createCarReportRecord({
                ufi: cleanedUfi,
                category: cleanedCategory,
                subject: cleanedSubject,
                comments: cleanedComment,
                creator,
                airport: cleanedAirport || undefined,
                chartRef: cleanedChartRef || undefined,
                isResponseRequired: form.requireResponse === 'YES',
                assigneeLead: cleanedLead || undefined,
                assigneeGroup: cleanedGroups.length > 0 ? cleanedGroups : undefined,
                photoIDs: photoIDs.length > 0 ? photoIDs : undefined,
            });

            assertApiSuccess(createResp, 'Create CAR report failed.', 'createReport');

            const carReportId = getCreatedId(createResp);
            if (!carReportId) {
                throw new Error('Missing car report ID after creating report.');
            }

            let remarkFailed = false;
            let remarkFailedMessage = '';
            let remarkAlreadyNotified = false;

            if (cleanedRemark || remarkAttachmentFiles.length > 0) {
                try {
                    let remarkAttachment: { fileId: string; name: string }[] = [];
                    if (remarkAttachmentFiles.length > 0) {
                        const remarkUploadResp = await retryRequest(
                            () =>
                                uploadFiles({
                                    files: remarkAttachmentFiles,
                                    path: `${cleanedUfi}/carsAdminAttachements/${carReportId}`,
                                    type: 'carReport',
                                }),
                            REMARK_MAX_ATTEMPTS,
                            (resp) => isRetryableResponse(resp),
                        );

                        assertApiSuccess(remarkUploadResp, 'Attachment upload failed.');

                        if (remarkUploadResp?.code !== 0 || !Array.isArray(remarkUploadResp?.data?.fileIds)) {
                            throw new Error(remarkUploadResp?.msg || remarkUploadResp?.message || 'Attachment upload failed.');
                        }

                        const uploadedRemarkFiles = remarkUploadResp?.data?.fileIds ?? [];
                        remarkAttachment = uploadedRemarkFiles
                            .map((x) => ({
                                fileId: normalizeUploadedFileId(x?.fileId),
                                name: String(x?.name ?? '').trim(),
                            }))
                            .filter((x) => x.fileId);
                    }

                    const remarkResp = await retryRequest(
                        () =>
                            createCarReportRemark({
                                carReportId,
                                content: cleanedRemark,
                                author: creator,
                                attachment: remarkAttachment.length > 0 ? remarkAttachment : undefined,
                            }),
                        REMARK_MAX_ATTEMPTS,
                        (resp) => isRetryableResponse(resp),
                    );

                    assertApiSuccess(remarkResp, 'Create remark failed.');
                } catch (remarkErr) {
                    remarkFailed = true;
                    remarkAlreadyNotified = !shouldShowLocalErrorToast(remarkErr);
                    const detail = extractErrorMessage(remarkErr, 'Create remark failed.');
                    remarkFailedMessage = detail.toLowerCase().includes('attachment upload failed')
                        ? detail
                        : formatAddRemarkErrorMessage(remarkErr);
                }
            }

            const nowText = moment.utc().format('DD MMM YYYY HH:mm');
            appDispatch(
                pushToast({
                    type: 'success',
                    message: `${cleanedUfi} is success created at ${nowText}`,
                }),
            );

            if (remarkFailed && !remarkAlreadyNotified) {
                window.setTimeout(() => {
                    appDispatch(
                        pushToast({
                            type: 'error',
                            message: remarkFailedMessage || 'Create remark failed.',
                        }),
                    );
                }, 0);
            }

            history.push(REPORTS_PATHS.carReportTracker);
        } catch (e) {
            if (shouldShowLocalErrorToast(e)) {
                const message = (e as CreateFlowError)?.source === 'createReport'
                    ? formatCreateReportErrorMessage(e)
                    : extractErrorMessage(e, 'Create CAR report failed.');
                appDispatch(pushToast({ type: 'error', message }));
            }
        } finally {
            setCreating(false);
        }
    }, [appDispatch, creating, creator, form, history, photoFiles, remarkAttachmentFiles, validateMandatoryFields]);

    return (
        <div className="car-report-create-page carReportTopicPageModal">
            <div className="breadcrumbs car-report-create-page__breadcrumbs">
                <Link
                    to={REPORTS_PATHS.carReportTracker}
                    className="car-report-create-page__breadcrumb-link"
                    onClick={handleBreadcrumbTrackerClick}
                >
                    CAR Report Tracker
                </Link>{' '}
                /<span className="car-report-create-page__breadcrumb-current">Create new CAR report</span>
            </div>

            <div className="CathaySansBd car-report-create-page__heading">
                Create new CAR report
            </div>

            <div className="car-report-create-page__container">
                <div className="car-report-create-page__section car-report-create-page__section--tight-bottom">
                    <div className="car-report-create-page__section-heading">Report details</div>
                    <div className="car-report-create-page__section-content car-report-create-page__section-content--compact-gap">
                        <CRow className="car-report-create-page__row">
                            <CCol xs={12} md={6}>
                                <div className="car-report-create-page__field-pair-middle">
                                    <div className="car-report-create-page__label">Category</div>
                                    <div className="car-report-create-page__field-value">
                                        <div className={errors.category ? 'errorBorder' : ''}>
                                            <CXDropDown
                                                zIndex={1030}
                                                value={form.category}
                                                placeholder="Category"
                                                onChange={(v: string) => handleFormChange('category', v)}
                                                options={categoryOptions}
                                            />
                                        </div>
                                        <ErrorText text={errors.category} />
                                    </div>
                                </div>
                            </CCol>
                            <CCol xs={12} md={6} className="car-report-create-page__col--subject">
                                <div className="car-report-create-page__field-pair-middle">
                                    <div className="car-report-create-page__label">Subject</div>
                                    <div className="car-report-create-page__field-value">
                                        <div className={errors.subject ? 'errorBorder' : ''}>
                                            <CXDropDown
                                                zIndex={1030}
                                                value={form.subject}
                                                placeholder="Subject"
                                                onChange={(v: string) => handleFormChange('subject', v)}
                                                options={subjectOptions}
                                            />
                                        </div>
                                        <ErrorText text={errors.subject} />
                                    </div>
                                </div>
                            </CCol>
                        </CRow>
                        <CRow>
                            <CCol xs={12}>
                                <div className="car-report-create-page__field-pair-top">
                                    <div className="car-report-create-page__label">Comment</div>
                                    <div className="car-report-create-page__field-value">
                                        <div className={errors.comment ? 'errorBorder' : ''}>
                                            <CXTextArea
                                                minRows={3}
                                                maxRows={8}
                                                zIndex={1000}
                                                placeholder="Comment"
                                                value={form.comment}
                                                onChange={(v: string) => handleFormChange('comment', v)}
                                            />
                                        </div>
                                        <ErrorText text={errors.comment} />
                                    </div>
                                </div>
                            </CCol>
                        </CRow>
                    </div>
                </div>

                <div className="car-report-create-page__section car-report-create-page__section--tight-bottom">
                    <div className="car-report-create-page__section-heading">Flight details</div>
                    <div className="car-report-create-page__section-content">
                        <CRow>
                            <CCol xs={12} md={6}>
                                <div className="car-report-create-page__field-pair-middle car-report-create-page__field-pair-middle--ufi">
                                    <div className="car-report-create-page__label">UFI</div>
                                    <div className="car-report-create-page__field-value">
                                        <div className={errors.ufi ? 'errorBorder' : ''}>
                                            <CXTextInput
                                                zIndex={1000}
                                                value={form.ufi}
                                                placeholder="UFI ( Sample: CPA041103NOV2025RKSIVHHH )"
                                                onChange={(v: string) => handleFormChange('ufi', v)}
                                            />
                                        </div>
                                        <ErrorText text={errors.ufi} />
                                    </div>
                                </div>
                            </CCol>
                        </CRow>
                    </div>
                </div>

                <div className="car-report-create-page__section">
                    <div className="car-report-create-page__section-heading">Supporting information (Optional)</div>
                    <div className="car-report-create-page__section-content">
                        <CRow className="car-report-create-page__row">
                            <CCol xs={12} md={6}>
                                <div className="car-report-create-page__field-pair-middle">
                                    <div className="car-report-create-page__label">Airport</div>
                                    <div className="car-report-create-page__field-value">
                                        <CXTextInput
                                            zIndex={1000}
                                            value={form.airport}
                                            placeholder="Airport"
                                            onChange={(v: string) => handleFormChange('airport', v)}
                                        />
                                    </div>
                                </div>
                            </CCol>
                            <CCol xs={12} md={6}>
                                <div className="car-report-create-page__field-pair-middle">
                                    <div className="car-report-create-page__label">Chart Ref</div>
                                    <div className="car-report-create-page__field-value">
                                        <CXTextInput
                                            zIndex={1000}
                                            value={form.chartRef}
                                            placeholder="Chart Ref"
                                            onChange={(v: string) => handleFormChange('chartRef', v)}
                                        />
                                    </div>
                                </div>
                            </CCol>
                        </CRow>
                        <CRow className="car-report-create-page__row">
                            <CCol xs={12} md={6}>
                                <div className="car-report-create-page__field-pair-middle car-report-create-page__field-pair-middle--center">
                                    <div className="car-report-create-page__label">Require response</div>
                                    <div className="car-report-create-page__field-value">
                                        <div className="car-report-create-page__radio-group">
                                            <label className="car-report-create-page__radio-label">
                                                <input
                                                    type="radio"
                                                    name="requireResponse"
                                                    value="YES"
                                                    checked={form.requireResponse === 'YES'}
                                                    onChange={() => handleRequireResponseChange('YES')}
                                                    disabled={creating}
                                                />
                                                <span>Yes</span>
                                            </label>
                                            <label className="car-report-create-page__radio-label">
                                                <input
                                                    type="radio"
                                                    name="requireResponse"
                                                    value="NO"
                                                    checked={form.requireResponse === 'NO'}
                                                    onChange={() => handleRequireResponseChange('NO')}
                                                    disabled={creating}
                                                />
                                                <span>No</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </CCol>
                        </CRow>
                        <CRow>
                            <CCol xs={12}>
                                <div className="car-report-create-page__field-pair-top">
                                    <div className="car-report-create-page__label">Photo</div>
                                    <div className="car-report-create-page__field-value">
                                        <UploadField
                                            title="Uploaded file"
                                            files={photoFiles}
                                            disabled={creating}
                                            accept={PHOTO_ACCEPT_ATTR}
                                            inputTestId="car-report-photo-upload-input"
                                            onFilesAdded={handlePhotoFilesAdded}
                                            onRemoveFile={(idx) => setPhotoFiles((prev) => prev.filter((_, i) => i !== idx))}
                                        />
                                    </div>
                                </div>
                            </CCol>
                        </CRow>
                    </div>
                </div>

                <div className="car-report-create-page__section">
                    <div className="car-report-create-page__section-heading">Assignee details (Optional)</div>
                    <div className="car-report-create-page__section-content">
                        <CRow>
                            <CCol xs={12} md={6}>
                                <div className="car-report-create-page__field-pair-middle car-report-create-page__field-pair-middle--center">
                                    <div className="car-report-create-page__label">Assignee Lead</div>
                                    <div className="car-report-create-page__field-value">
                                        <MultiSelectClearDropdown
                                            key={`assignee-lead-${form.assigneeLead}`}
                                            resetKey={`assignee-lead-${form.assigneeLead}`}
                                            values={selectedLeadOptions}
                                            onChange={setForm}
                                            defaultOptions={leadOptions}
                                            onChangeField={onChangeAssigneeField}
                                            field="assigneeLead"
                                            state={form}
                                            zIndex={1028}
                                            placeholder="Assignee lead"
                                            selectAll={false}
                                        />
                                    </div>
                                </div>
                            </CCol>
                            <CCol xs={12} md={6}>
                                <div className="car-report-create-page__field-pair-middle car-report-create-page__field-pair-middle--center">
                                    <div className="car-report-create-page__label">Assignee Group</div>
                                    <div className="car-report-create-page__field-value">
                                        <MultiSelectClearDropdown
                                            key={`assignee-group-${form.assigneeGroup.join('|')}`}
                                            resetKey={`assignee-group-${form.assigneeGroup.join('|')}`}
                                            values={selectedGroupOptions}
                                            onChange={setForm}
                                            defaultOptions={groupOptions}
                                            onChangeField={onChangeAssigneeField}
                                            field="assigneeGroup"
                                            state={form}
                                            zIndex={1027}
                                            placeholder="Assignee group"
                                            selectAll={false}
                                        />
                                    </div>
                                </div>
                            </CCol>
                        </CRow>
                    </div>
                </div>

                <div className="car-report-create-page__section">
                    <div className="car-report-create-page__section-heading">Remarks (Optional)</div>
                    <div className="car-report-create-page__section-content car-report-create-page__section-content--remarks-gap">
                        <CRow className="car-report-create-page__row">
                            <CCol xs={12}>
                                <div className="car-report-create-page__field-pair-top">
                                    <div className="car-report-create-page__label">Remarks</div>
                                    <div className="car-report-create-page__field-value">
                                        <div className={remarkErrors.content ? 'errorBorder' : ''}>
                                            <CXTextArea
                                                minRows={2}
                                                maxRows={8}
                                                zIndex={1000}
                                                placeholder="Remarks"
                                                value={form.remark}
                                                onChange={(v: string) => handleFormChange('remark', v)}
                                            />
                                        </div>
                                        <ErrorText text={remarkErrors.content ? 'Remarks required if files are attached.' : undefined} />
                                    </div>
                                </div>
                            </CCol>
                        </CRow>
                        <CRow>
                            <CCol xs={12}>
                                <div className="car-report-create-page__field-pair-top">
                                    <div className="car-report-create-page__label">Attachment</div>
                                    <div className="car-report-create-page__field-value">
                                        <UploadField
                                            title="Uploaded file"
                                            files={remarkAttachmentFiles}
                                            disabled={creating}
                                            onFilesAdded={(files) => {
                                                setRemarkAttachmentFiles((prev) => [...prev, ...files]);
                                            }}
                                            onRemoveFile={(idx) => {
                                                setRemarkAttachmentFiles((prev) => prev.filter((_, i) => i !== idx));
                                            }}
                                        />
                                    </div>
                                </div>
                            </CCol>
                        </CRow>
                    </div>
                </div>

                <div className="car-report-create-page__actions">
                    <CXButton
                        outline
                        text="Cancel"
                        onClick={openCancelConfirm}
                        fontSize="14px"
                        width="120px"
                        disabled={creating}
                    />
                    <CXButton
                        text={creating ? 'Creating...' : 'Create'}
                        onClick={handleCreate}
                        fontSize="14px"
                        width="120px"
                        disabled={creating}
                    />
                </div>
            </div>

            <Modal show={showCancelConfirm} onHide={() => setShowCancelConfirm(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Cancel new CAR report creation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div
                        style={{
                            fontSize: 14,
                            lineHeight: '20px',
                            color: '#4C4C4C',
                        }}
                    >
                        Are you sure to cancel the new CAR report creation? All inputs will be removed.
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div style={{ width: 200 }}>
                        <CXButton
                            outline
                            text="Back"
                            onClick={handleCancelModalBack}
                            fontSize="16px"
                            width="100%"
                        />
                    </div>
                    <div style={{ width: 200 }}>
                        <CXButton
                            text="Confirm"
                            onClick={handleCancelModalConfirm}
                            fontSize="16px"
                            width="100%"
                        />
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
